import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useState,
} from "react";
import { useNavigate } from "react-router-dom";
import { requestFcmToken } from "../../services/fcm";
import { subscribeToForegroundMessages } from "../../firebase";

interface NotificationContextType {
  unreadCount: number;
  incrementUnread: () => void;
  resetUnread: () => void;
  setUnread: (count: number) => void;
}

const NotificationContext = createContext<NotificationContextType | undefined>(
  undefined
);

export const useNotification = () => {
  const ctx = useContext(NotificationContext);
  // Return default values if context is not available (instead of throwing)
  if (!ctx) {
    return {
      unreadCount: 0,
      incrementUnread: () => {},
      resetUnread: () => {},
      setUnread: () => {},
    };
  }
  return ctx;
};

export const NotificationProvider: React.FC<{ children: React.ReactNode }> = ({
  children,
}) => {
  const [unreadCount, setUnreadCount] = useState(0);
  const navigate = useNavigate();

  const incrementUnread = () => setUnreadCount((c) => c + 1);
  const resetUnread = () => setUnreadCount(0);
  const setUnread = (count: number) => setUnreadCount(count);

  const showBrowserNotification = useCallback(
    (title: string, body: string, url?: string) => {
    if (typeof window === "undefined" || !("Notification" in window)) return;
    if (Notification.permission !== "granted") return;

    try {
      const notification = new Notification(title, {
        body,
        icon: "/logo192.png",
        data: { url },
      });
      notification.onclick = () => {
        window.focus();
        if (url) {
          navigate(url);
        }
        notification.close();
      };
    } catch (error) {
      console.warn("Failed to display notification:", error);
    }
    },
    [navigate]
  );

  useEffect(() => {
    const attemptTokenRegistration = () => {
      try {
        const storedUser = JSON.parse(localStorage.getItem("user") || "null");
        if (storedUser?.id && storedUser?.userType === "CLIENT") {
          requestFcmToken(String(storedUser.id)).catch((error) =>
            console.warn("FCM token registration failed:", error)
          );
        }
      } catch (error) {
        console.warn("Unable to parse stored user for FCM:", error);
      }
    };

    attemptTokenRegistration();

    const handleStorage = (event: StorageEvent) => {
      if (event.key === "user" || event.key === null) {
        attemptTokenRegistration();
      }
    };

    const handleCustomStorageChange = () => attemptTokenRegistration();

    window.addEventListener("storage", handleStorage);
    window.addEventListener("localStorageChange", handleCustomStorageChange);

    return () => {
      window.removeEventListener("storage", handleStorage);
      window.removeEventListener("localStorageChange", handleCustomStorageChange);
    };
  }, []);

  useEffect(() => {
    let unsubscribe: (() => void) | undefined;

    subscribeToForegroundMessages((payload) => {
      const title =
        payload?.notification?.title ||
        payload?.data?.title ||
        "New EventNova message";
      const body =
        payload?.notification?.body ||
        payload?.data?.body ||
        "You have a new chat update.";
      const url = payload?.data?.url;

      showBrowserNotification(title, body, url);
      setUnreadCount((count) => count + 1);
    })
      .then((fn) => {
        unsubscribe = fn;
      })
      .catch((error) =>
        console.warn("Failed to subscribe to foreground FCM messages:", error)
      );

    return () => {
      if (unsubscribe) unsubscribe();
    };
  }, [showBrowserNotification]);

  useEffect(() => {
    if (typeof window === "undefined" || !("serviceWorker" in navigator)) {
      return;
    }

    const handleSwMessage = (event: MessageEvent) => {
      if (event.data?.type === "FCM_NOTIFICATION_CLICKED") {
        const targetUrl = event.data?.payload?.url;
        if (targetUrl) {
          navigate(targetUrl);
        }
      }
    };

    navigator.serviceWorker.addEventListener("message", handleSwMessage);

    return () => {
      navigator.serviceWorker.removeEventListener("message", handleSwMessage);
    };
  }, [navigate]);

  return (
    <NotificationContext.Provider
      value={{ unreadCount, incrementUnread, resetUnread, setUnread }}
    >
      {children}
    </NotificationContext.Provider>
  );
};
